import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronUp, FiHelpCircle } = FiIcons;

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const faqs = [
    {
      question: "What services does Tutor Hub Online offer?",
      answer: "We provide two main services: Assignment Support (help with essays, dissertations, research papers, and academic writing) and Tutoring Services (one-to-one and group sessions across various subjects and curriculum levels including Cambridge, IB, and IEB)."
    },
    {
      question: "How do I book a tutoring session?",
      answer: "You can book a tutoring session by visiting our Contact page, calling us directly, or using our online booking system. We'll match you with the most suitable tutor based on your subject and curriculum needs."
    },
    {
      question: "What subjects do you cover?",
      answer: "We cover a wide range of subjects including Mathematics & Statistics, Science subjects (Physics, Chemistry, Biology), Business & Economics, Computer Science & Engineering, Humanities & Social Sciences, and Languages including English and Academic Writing Skills."
    },
    {
      question: "Are your services confidential?",
      answer: "Yes, absolutely. We ensure complete confidentiality of all student information. Your personal data and academic details will not be shared with third parties without your consent."
    },
    {
      question: "What curriculum levels do you support?",
      answer: "We support multiple curriculum levels including Cambridge (Primary, Lower Secondary, IGCSE, AS/A Levels), IB (PYP, MYP, DP), IEB programs, National & International Boards, and University-level subjects."
    },
    {
      question: "How much do your services cost?",
      answer: "Our pricing is student-friendly and affordable. Costs vary depending on the type of service (assignment support vs tutoring), subject complexity, and urgency. Contact us for a personalized quote based on your specific needs."
    },
    {
      question: "Do you provide revisions for assignments?",
      answer: "Yes, we provide revisions for assignments if requested within a reasonable period, based on the original instructions provided. This ensures you receive exactly what you need for your academic success."
    },
    {
      question: "How quickly can you help with urgent assignments?",
      answer: "We understand that students often face tight deadlines. We offer urgent assignment support and can often accommodate same-day or next-day delivery depending on the complexity and length of the work required."
    },
    {
      question: "Are your tutors qualified?",
      answer: "Yes, all our tutors are qualified professionals with advanced degrees in their respective fields. They have extensive experience in teaching and academic support, ensuring you receive expert guidance."
    },
    {
      question: "Can I get a free consultation?",
      answer: "Yes, we offer free consultations to understand your needs and explain how our services can help you achieve your academic goals. This allows us to match you with the right tutor or support service."
    },
    {
      question: "Is online tutoring as effective as in-person tutoring?",
      answer: "Yes, our online tutoring sessions are highly effective. We use interactive tools, real-time feedback, and personalized teaching methods that often provide more flexibility and convenience than traditional in-person sessions."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept various payment methods including credit cards, debit cards, and online payment platforms. All payments must be completed in advance before services are delivered, unless otherwise agreed."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Frequently Asked Questions
          </h1>
          <h2 className="text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto">
            Find answers to common questions about our assignment support and tutoring services.
          </h2>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  <SafeIcon 
                    icon={openItems[index] ? FiChevronUp : FiChevronDown} 
                    className="w-5 h-5 text-gray-500 flex-shrink-0" 
                  />
                </button>
                {openItems[index] && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 bg-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiHelpCircle} className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Still have questions?</h2>
            <p className="text-gray-600 mb-6">
              Can't find the answer you're looking for? Our friendly support team is here to help you with any questions about our services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Contact Support
              </Link>
              <a
                href="mailto:info@tutorhubonline.com"
                className="border-2 border-primary text-primary px-6 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition-colors"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to get started?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join thousands of students who have improved their grades and academic confidence with Tutor Hub Online.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/assignment-support"
              className="bg-accent text-black px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
            >
              Get Assignment Support
            </Link>
            <Link
              to="/book-tutor"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors"
            >
              Book a Tutor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;